import React, { useMemo, useState } from 'react';
import { Tab, Tabs } from '@mui/material';
import Grid from '@mui/material/Grid';
import DataEmptyMessage from '@components/common/DataEmptyMessage';
import DataLoadingError from '@components/common/DataLoadingError';
import OverlaySpinner from '@components/common/OverlaySpinner';
import moment from '@lib/moment';
import SessionsTable from '../SessionsTable';
import useSessionsQuery from '@components/common/hooks/api/useSessionsQuery';
import SessionFilters from '@components/ContentComponents/Sessions/SessionFilters';
import { useNavigate, useSearchParams } from 'react-router-dom';

const COLUMNS = ['start', 'curriculum', 'session_name', 'delivery_site', 'created_by'];

const UpcomingSessions = () => {
  const [currentTab, setCurrentTab] = useState('community');
  const [searchParams] = useSearchParams();

  // Timestamp when the component was first rendered. Prevents infinite query loops.
  const now = useMemo(() => moment().toISOString(), []);

  const filters = {
    program_areas: searchParams.getAll('program_areas'),
    delivery_location: searchParams.getAll('delivery_location'),
    created_by: searchParams.getAll('created_by'),
  };

  const {
    data,
    loading,
    error,
    isError,
  } = useSessionsQuery({
    filters: {
      ...filters,
      'status:not': 'CANCELED',
      'end:gte': now,
    }
  });

  const sessions = useMemo(() => {
    if (!data || data.length < 1) {
      return null;
    }
    const sessions = {
      community: [],
      test: [],
    };
    data.forEach((session) => {
      if (session.event.is_test) {
        sessions.test.push(session);
      } else {
        sessions.community.push(session);
      }
    });
    return sessions;
  }, [data]);

  return (
    <div id="sessions" style={{ position: 'relative' }}>
      <SessionFilters />
      <OverlaySpinner condition={loading} fixed />
      <Grid container spacing={1}>
        { !loading && !isError && (!!sessions > 0 ? (
          <Grid item xs={12}>
            {/* Note: Bottom margin on the Tabs makes the spacing consistent with the other
            list pages, which show the status legend, while this page doesn't. */}
            <Tabs
              value={currentTab}
              onChange={(_, newValue) => setCurrentTab(newValue)}
              sx={{marginBottom: '40px'}}
            >
              <Tab
                label="Community Sessions"
                value="community"
                data-cy="tab-community"
                id="community-sessions-tab"
                aria-controls="community-sessions-panel"
              />
              <Tab
                label="Test Sessions"
                value="test"
                data-cy="tab-test"
              />
            </Tabs>
            {currentTab === 'community' && (
              <div
                role="tabpanel"
                id="community-sessions-panel"
                aria-labelledby="community-tab"
                aria-hidden={currentTab !== 'community' ? 'true' : 'false'}
              >
                {sessions.community.length ? (
                  <SessionsTable sessions={sessions.community} columns={COLUMNS} legend={false} />
                ) : (
                  <DataEmptyMessage title={"You have no upcoming community sessions."} />
                )}
              </div>
            )}
            {currentTab === 'test' && (
              <div
                role="tabpanel"
                id="test-sessions-panel"
                aria-labelledby="test-tab"
                aria-hidden={currentTab !== 'test' ? 'true' : 'false'}
              >
                {sessions.test.length ? (
                  <SessionsTable sessions={sessions.test} columns={COLUMNS} legend={false} />
                ) : (
                  <DataEmptyMessage title={"You have no upcoming test sessions."} />
                )}
              </div>
            )}
          </Grid>
        ) : (
          <DataEmptyMessage title={"You have no upcoming sessions."} />
        ))}

        {isError && (
          <DataLoadingError title={"Failed to load sessions!"} errors={error.errors} />
        )}
      </Grid>
    </div>
  );
};

export default UpcomingSessions;
